/* ===========================================================================
  Slide-out cart (sections/cart-drawer.liquid) and the sticky add to cart bar
  (snippets/sticky-add-to-cart.liquid), built into the theme instead of a cart app.

  Every cart change goes through the Ajax Cart API with `sections` set, so the
  response carries freshly rendered drawer markup: prices, the free shipping
  bar and the add-ons all come from Liquid, nothing is formatted here.
  Without a drawer on the page (cart page, or the setting off) add to cart
  forms and the cart icon behave as before.
============================================================================ */
(function () {
  if (window.cartDrawer) return;

  var SECTION = 'cart-drawer';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Where focus goes when the focused element's whole group disappears after a re-render.
  var GROUP_FALLBACK = { code: 'coupon-input', upsell: 'checkout', offer: 'checkout' };

  var queue = Promise.resolve();
  var pending = {}; // line key -> { quantity, timer } for +/- clicks not sent yet
  var opener = null;
  var pointerDownOnBackdrop = false;

  function drawer() {
    return document.getElementById('CartDrawer');
  }

  function content() {
    var el = drawer();
    return el && el.querySelector('[data-cart-drawer-content]');
  }

  function text(name, values) {
    var el = drawer();
    var str = (el && el.dataset['text' + name]) || '';
    Object.keys(values || {}).forEach(function (key) {
      str = str.split('[' + key + ']').join(values[key]);
    });
    return str;
  }

  function errorMessage(error) {
    var data = error && error.data;
    return (data && (data.description || data.message)) || text('Error');
  }

  /* ---------------------------------------------------------------------------
    Requests
  --------------------------------------------------------------------------- */

  function parse(response) {
    return response.json().catch(function () { return {}; }).then(function (data) {
      if (!response.ok) {
        var error = new Error(data.description || data.message || response.statusText);
        error.data = data;
        throw error;
      }
      return data;
    });
  }

  function post(path, body) {
    var isForm = body instanceof FormData;
    if (isForm) body.append('sections', SECTION);
    else body.sections = SECTION;

    return fetch(ROOT + path, {
      method: 'POST',
      headers: isForm
        ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body)
    }).then(parse);
  }

  // One cart request at a time, so responses render in the order they were made.
  function enqueue(task) {
    var run = queue.then(task);
    queue = run.catch(function () {});
    return run;
  }

  function sectionHtml(response) {
    return response && response.sections && response.sections[SECTION];
  }

  /* ---------------------------------------------------------------------------
    Rendering
  --------------------------------------------------------------------------- */

  function render(html) {
    var current = content();
    if (!html || !current) return;

    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-content]');
    if (!fresh) return;

    // Remember what the shopper was doing so the swap is invisible to them.
    var focus = null;
    var active = document.activeElement;
    if (active && current.contains(active)) {
      var group = active.closest('[data-focus-group]');
      var groupName = group && group.getAttribute('data-focus-group');
      focus = {
        key: active.getAttribute('data-focus-key'),
        group: groupName,
        index: group ? Array.prototype.indexOf.call(current.querySelectorAll('[data-focus-group="' + groupName + '"]'), group) : -1
      };
    }
    var scroller = current.querySelector('[data-cart-drawer-scroll]');
    var scrollTop = scroller ? scroller.scrollTop : 0;
    var coupon = current.querySelector('[data-cart-drawer-coupon]');
    var couponOpen = coupon && coupon.open;
    var upsells = current.querySelector('[data-cart-drawer-upsell-list]');
    var upsellsLeft = upsells ? upsells.scrollLeft : 0;

    // Line images are lazy in the page's initial (closed) drawer; once it is in use, load them now.
    fresh.querySelectorAll('.cart-drawer__item-image img').forEach(function (img) {
      img.loading = 'eager';
    });
    current.replaceWith(fresh);

    var freshScroller = fresh.querySelector('[data-cart-drawer-scroll]');
    if (freshScroller) freshScroller.scrollTop = scrollTop;
    var freshCoupon = fresh.querySelector('[data-cart-drawer-coupon]');
    if (freshCoupon && couponOpen) freshCoupon.open = true;
    var freshUpsells = fresh.querySelector('[data-cart-drawer-upsell-list]');
    if (freshUpsells) freshUpsells.scrollLeft = upsellsLeft;
    Object.keys(pending).forEach(function (key) {
      var input = fresh.querySelector('[data-cart-drawer-qty="' + CSS.escape(key) + '"]');
      if (input) input.value = pending[key].quantity;
    });

    updateCount(parseInt(fresh.getAttribute('data-item-count'), 10));
    updateUpsellNav(fresh);
    if (focus) restoreFocus(fresh, focus);
  }

  function restoreFocus(root, focus) {
    var target = focus.key && root.querySelector('[data-focus-key="' + CSS.escape(focus.key) + '"]');
    if (!target && focus.group) {
      var items = root.querySelectorAll('[data-focus-group="' + focus.group + '"]');
      var item = items[Math.min(focus.index, items.length - 1)];
      target = item && item.querySelector('[data-focus-key]');
      if (!target && GROUP_FALLBACK[focus.group]) {
        target = root.querySelector('[data-focus-key="' + GROUP_FALLBACK[focus.group] + '"]');
      }
    }
    (target || root.querySelector('#CartDrawer-title')).focus();
  }

  function updateCount(count) {
    if (isNaN(count)) return;
    document.querySelectorAll('.cart_count').forEach(function (el) {
      el.textContent = count;
    });
  }

  function busy(on) {
    var el = content();
    if (el) el.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function announce(message) {
    var status = drawer() && drawer().querySelector('[data-cart-drawer-status]');
    if (!status || !message) return;
    // Cleared first so the same message twice in a row is read out again.
    status.textContent = '';
    setTimeout(function () { status.textContent = message; }, 50);
  }

  function showError(message) {
    var el = content() && content().querySelector('[data-cart-drawer-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function clearError() {
    var el = content() && content().querySelector('[data-cart-drawer-error]');
    if (el) el.hidden = true;
  }

  // A failed request may have left the drawer out of step with the cart: re-sync, then explain.
  function fail(error) {
    var message = errorMessage(error);
    return refresh().catch(function () {}).then(function () {
      busy(false);
      showError(message);
    });
  }

  function refresh() {
    return enqueue(function () {
      return fetch(ROOT + '?sections=' + SECTION, { headers: { Accept: 'application/json' } }).then(parse);
    }).then(function (sections) {
      render(sections[SECTION]);
    });
  }

  /* ---------------------------------------------------------------------------
    Open and close
  --------------------------------------------------------------------------- */

  function open(trigger) {
    var el = drawer();
    if (!el || el.open) return;
    opener = trigger || document.activeElement;
    document.documentElement.classList.add('cart-drawer-open');
    el.showModal();
    // Two frames, so the slide-in starts from the off-screen position.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.classList.add('is-open');
        updateUpsellNav(el);
      });
    });
    var title = el.querySelector('#CartDrawer-title');
    if (title) title.focus();
  }

  function close() {
    var el = drawer();
    if (!el || !el.open || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    var duration = parseFloat(getComputedStyle(el).transitionDuration) || 0;
    if (!duration) {
      el.close();
      return;
    }
    var finish = function () {
      el.removeEventListener('transitionend', onEnd);
      if (el.open && !el.classList.contains('is-open')) el.close();
    };
    var onEnd = function (event) {
      if (event.target === el) finish();
    };
    el.addEventListener('transitionend', onEnd);
    setTimeout(finish, duration * 1000 + 100);
  }

  // `close` fires however the dialog closed (our button, Escape, a forced close).
  document.addEventListener('close', function (event) {
    if (event.target !== drawer()) return;
    event.target.classList.remove('is-open');
    document.documentElement.classList.remove('cart-drawer-open');
    if (opener && opener.isConnected) opener.focus();
    opener = null;
  }, true);

  // Escape: run the slide-out instead of the instant close.
  document.addEventListener('cancel', function (event) {
    if (event.target !== drawer()) return;
    event.preventDefault();
    close();
  }, true);

  /* ---------------------------------------------------------------------------
    Cart changes
  --------------------------------------------------------------------------- */

  function changeLine(key, quantity, title) {
    clearError();
    busy(true);
    return enqueue(function () {
      return post('cart/change.js', { id: key, quantity: quantity });
    }).then(function (cart) {
      render(sectionHtml(cart));
      var line = (cart.items || []).filter(function (item) { return item.key === key; })[0];
      if (quantity > 0 && line && line.quantity < quantity) {
        showError(text('Stock', { quantity: line.quantity }));
      }
      announce(quantity === 0 ? text('Removed', { title: title }) : text('Updated'));
    }).catch(fail);
  }

  function step(button) {
    var input = button.parentNode.querySelector('[data-cart-drawer-qty]');
    if (!input) return;
    var key = input.getAttribute('data-cart-drawer-qty');
    var quantity = Math.max(0, (parseInt(input.value, 10) || 0) + Number(button.getAttribute('data-cart-drawer-step')));
    input.value = quantity;
    // Rapid clicks become one request with the final quantity.
    if (pending[key]) clearTimeout(pending[key].timer);
    pending[key] = {
      quantity: quantity,
      timer: setTimeout(function () {
        delete pending[key];
        changeLine(key, quantity, input.getAttribute('data-title'));
      }, 400)
    };
  }

  function typedQuantity(input) {
    var key = input.getAttribute('data-cart-drawer-qty');
    var quantity = parseInt(input.value, 10);
    if (pending[key]) {
      clearTimeout(pending[key].timer);
      delete pending[key];
    }
    if (isNaN(quantity) || quantity < 0) {
      input.value = input.defaultValue;
      return;
    }
    if (input.getAttribute('data-sent') === String(quantity)) return;
    input.setAttribute('data-sent', quantity);
    changeLine(key, quantity, input.getAttribute('data-title'));
  }

  function addFromForm(form, submitter) {
    if (form.getAttribute('aria-busy') === 'true') return;
    var button = submitter || form.querySelector('[type="submit"], [name="add"]');
    var data;
    try {
      data = new FormData(form, submitter);
    } catch (error) {
      data = new FormData(form);
    }

    form.setAttribute('aria-busy', 'true');
    if (button) button.classList.add('disabled');
    var oldError = form.parentNode.querySelector('[data-cart-drawer-form-error]');
    if (oldError) oldError.remove();

    enqueue(function () {
      return post('cart/add.js', data);
    }).then(function (result) {
      render(sectionHtml(result));
      var item = result.items ? result.items[0] : result;
      open(button || form);
      announce(text('Added', { title: (item && item.product_title) || '' }));
    }).catch(function (error) {
      var message = document.createElement('p');
      message.className = 'warning cart-drawer-form-error';
      message.setAttribute('role', 'alert');
      message.setAttribute('data-cart-drawer-form-error', '');
      message.textContent = errorMessage(error);
      form.after(message);
    }).then(function () {
      form.removeAttribute('aria-busy');
      if (button) button.classList.remove('disabled');
    });
  }

  function addUpsell(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    clearError();
    busy(true);
    enqueue(function () {
      return post('cart/add.js', {
        items: [{ id: Number(button.getAttribute('data-cart-drawer-upsell-add')), quantity: 1 }]
      });
    }).then(function (result) {
      render(sectionHtml(result));
      announce(text('Added', { title: button.getAttribute('data-title') }));
    }).catch(fail);
  }

  /* ---------------------------------------------------------------------------
    Coupon codes (/cart/update.js with `discount`: the full list of codes)
  --------------------------------------------------------------------------- */

  function appliedCodes() {
    var el = content();
    return el ? Array.prototype.map.call(el.querySelectorAll('[data-cart-drawer-code]'), function (code) {
      return code.textContent.trim();
    }) : [];
  }

  function sameCode(a, b) {
    return a.toLowerCase() === b.toLowerCase();
  }

  function couponError(code, message) {
    var el = content();
    var details = el && el.querySelector('[data-cart-drawer-coupon]');
    if (!details) return;
    details.open = true;
    var input = details.querySelector('input[name="discount"]');
    var note = details.querySelector('[data-cart-drawer-coupon-error]');
    input.value = code;
    input.setAttribute('aria-invalid', 'true');
    note.textContent = message;
    note.hidden = false;
    input.focus();
    announce(message);
  }

  function applyCode(form) {
    var input = form.querySelector('input[name="discount"]');
    var code = input.value.trim();
    if (!code) {
      input.focus();
      return;
    }
    var codes = appliedCodes();
    if (codes.some(function (applied) { return sameCode(applied, code); })) {
      input.value = '';
      return;
    }

    clearError();
    busy(true);
    enqueue(function () {
      return post('cart/update.js', { discount: codes.concat(code).join(',') }).then(function (cart) {
        var entry = (cart.discount_codes || []).filter(function (discount) { return sameCode(discount.code, code); })[0];
        if (entry && entry.applicable) return { cart: cart, code: entry.code };
        // Shopify keeps a code that doesn't apply on the cart: take it off again.
        return post('cart/update.js', { discount: codes.join(',') }).then(function (clean) {
          return { cart: clean, code: null };
        });
      });
    }).then(function (result) {
      render(sectionHtml(result.cart));
      if (result.code) announce(text('CodeApplied', { code: result.code }));
      else couponError(code, text('CodeInvalid', { code: code }));
    }).catch(fail);
  }

  function removeCode(button) {
    var code = button.getAttribute('data-cart-drawer-remove-code');
    var codes = appliedCodes().filter(function (applied) { return !sameCode(applied, code); });
    clearError();
    busy(true);
    enqueue(function () {
      return post('cart/update.js', { discount: codes.join(',') });
    }).then(function (cart) {
      render(sectionHtml(cart));
      announce(text('Updated'));
    }).catch(fail);
  }

  /* ---------------------------------------------------------------------------
    Add-on carousel arrows (mouse only; keyboard and touch scroll the list itself)
  --------------------------------------------------------------------------- */

  function updateUpsellNav(root) {
    var list = root && root.querySelector('[data-cart-drawer-upsell-list]');
    if (!list) return;
    var prev = root.querySelector('[data-cart-drawer-scroll-upsells="-1"]');
    var next = root.querySelector('[data-cart-drawer-scroll-upsells="1"]');
    if (prev) prev.disabled = list.scrollLeft <= 2;
    if (next) next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
  }

  function scrollUpsells(button) {
    var section = button.closest('.cart-drawer__upsells');
    var list = section && section.querySelector('[data-cart-drawer-upsell-list]');
    if (!list) return;
    list.scrollBy({ left: (list.clientWidth - 60) * Number(button.getAttribute('data-cart-drawer-scroll-upsells')), behavior: 'smooth' });
  }

  document.addEventListener('scroll', function (event) {
    var target = event.target;
    if (target instanceof Element && target.matches('[data-cart-drawer-upsell-list]')) {
      updateUpsellNav(target.closest('.cart-drawer__upsells'));
    }
  }, true);

  /* ---------------------------------------------------------------------------
    Events
  --------------------------------------------------------------------------- */

  document.addEventListener('pointerdown', function (event) {
    pointerDownOnBackdrop = event.target === drawer();
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var el;

    // A click on the dialog box itself (not its content) is a click on the backdrop.
    if (target === drawer()) {
      if (pointerDownOnBackdrop) close();
      return;
    }
    if ((el = target.closest('a.mini_cart, [data-cart-drawer-open]'))) {
      if (!drawer() || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      open(el);
      return;
    }
    if (!drawer() || !drawer().contains(target)) return;

    if (target.closest('[data-cart-drawer-close]')) close();
    else if ((el = target.closest('[data-cart-drawer-step]'))) step(el);
    else if ((el = target.closest('[data-cart-drawer-remove]'))) {
      changeLine(el.getAttribute('data-cart-drawer-remove'), 0, el.getAttribute('data-title'));
    } else if ((el = target.closest('[data-cart-drawer-upsell-add]'))) addUpsell(el);
    else if ((el = target.closest('[data-cart-drawer-remove-code]'))) removeCode(el);
    else if ((el = target.closest('[data-cart-drawer-scroll-upsells]'))) scrollUpsells(el);
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-cart-drawer-qty]')) {
      typedQuantity(target);
    } else if (target.matches('[data-cart-drawer-upsell-variant]')) {
      // Add-on cards and offer cards: the add button follows the chosen option.
      var card = target.closest('[data-cart-drawer-upsell], [data-cart-drawer-offer]');
      var option = target.options[target.selectedIndex];
      var price = card.querySelector('[data-cart-drawer-upsell-price]');
      if (price && option.getAttribute('data-price')) price.textContent = option.getAttribute('data-price');
      card.querySelector('[data-cart-drawer-upsell-add]').setAttribute('data-cart-drawer-upsell-add', target.value);
    }
  });

  document.addEventListener('keydown', function (event) {
    var target = event.target;
    if (event.key === 'Enter' && target instanceof Element && target.matches('[data-cart-drawer-qty]')) {
      event.preventDefault();
      typedQuantity(target);
    }
  });

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (target instanceof Element && target.matches('[data-cart-drawer-coupon-form] input')) {
      target.removeAttribute('aria-invalid');
      var note = target.closest('[data-cart-drawer-coupon]').querySelector('[data-cart-drawer-coupon-error]');
      if (note) note.hidden = true;
    }
  });

  // Bubble phase, so a form's own submit handlers (validation) run first and can cancel.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (event.defaultPrevented || !drawer() || !(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-cart-drawer-coupon-form]')) {
      event.preventDefault();
      applyCode(form);
      return;
    }
    var path;
    try {
      path = new URL(form.getAttribute('action') || '', window.location.href).pathname;
    } catch (error) {
      return;
    }
    if (!/\/cart\/add(\.js)?$/.test(path)) return;
    event.preventDefault();
    addFromForm(form, event.submitter);
  });

  // Coming back from checkout with the browser's back button shows a cached page.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && drawer()) refresh().catch(function () {});
  });

  // Apps that call Shopify's standard openCart action get this drawer, not the cart page.
  function configureOpenCart() {
    var action = window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart;
    if (!action || typeof action.configure !== 'function' || !drawer()) return false;
    try {
      action.configure({
        handler: function () {
          return refresh().then(function () { open(); });
        }
      });
    } catch (error) {
      // Another script configured it first; its handler stays.
    }
    return true;
  }
  if (!configureOpenCart()) document.addEventListener('DOMContentLoaded', configureOpenCart, { once: true });

  // Theme editor: selecting the cart section or one of its offers opens the drawer in the preview.
  if (window.Shopify && window.Shopify.designMode) {
    var isCartSection = function (event) {
      return event.detail && event.detail.sectionId === SECTION;
    };
    ['shopify:section:select', 'shopify:block:select', 'shopify:section:load'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        if (isCartSection(event)) open();
      });
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      if (isCartSection(event)) close();
    });
  }

  /* ---------------------------------------------------------------------------
    Sticky add to cart bar
  --------------------------------------------------------------------------- */

  function initStickyBar() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar || !('IntersectionObserver' in window)) return;
    var productForm = document.getElementById(bar.getAttribute('data-sticky-atc'));
    var buttons = productForm && (productForm.querySelector('.purchase-details__buttons') || productForm);
    if (!buttons) return;

    var select = bar.querySelector('[data-sticky-atc-variant]');
    var quantity = bar.querySelector('[data-sticky-atc-quantity]');
    var submit = bar.querySelector('[data-sticky-atc-submit]');
    var price = bar.querySelector('[data-sticky-atc-price]');
    var image = bar.querySelector('[data-sticky-atc-image]');

    // Shown only once the main buttons have scrolled up out of view. The root reaches far below the
    // screen, so its only edge is the top of the viewport: a jump straight past the buttons (an
    // anchor link, a restored scroll position) still crosses it and fires.
    new IntersectionObserver(function (entries) {
      var entry = entries[entries.length - 1];
      var show = !entry.isIntersecting;
      bar.classList.toggle('is-visible', show);
      // The bar's height lifts the chat bubble clear of it (CSS in the snippet).
      if (show) document.documentElement.style.setProperty('--sticky-atc-height', bar.offsetHeight + 'px');
      document.body.classList.toggle('sticky-atc-visible', show);
    }, { rootMargin: '0px 0px 100000px 0px' }).observe(buttons);

    function syncVariant() {
      if (!select) return;
      var option = select.options[select.selectedIndex];
      if (price) price.innerHTML = option.getAttribute('data-price-html');
      if (image && option.getAttribute('data-image')) {
        image.removeAttribute('srcset');
        image.src = option.getAttribute('data-image');
      }
      submit.disabled = option.disabled;
      submit.querySelector('[data-sticky-atc-label]').textContent = option.disabled
        ? submit.getAttribute('data-sold-out')
        : submit.getAttribute('data-add');
    }

    if (select) {
      select.addEventListener('change', syncVariant);
      // Follow the variant picked in the main product form.
      productForm.addEventListener('variant-change', function (event) {
        var variant = event.detail && event.detail.variant;
        if (!variant || String(select.value) === String(variant.id)) return;
        if (!select.querySelector('option[value="' + variant.id + '"]')) return;
        select.value = String(variant.id);
        syncVariant();
      });
    }

    bar.addEventListener('click', function (event) {
      var button = event.target instanceof Element && event.target.closest('[data-sticky-atc-step]');
      if (!button || !quantity) return;
      var value = (parseInt(quantity.value, 10) || 1) + Number(button.getAttribute('data-sticky-atc-step'));
      quantity.value = Math.max(1, value);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initStickyBar, { once: true });
  else initStickyBar();

  window.cartDrawer = {
    open: open,
    close: close,
    refresh: refresh
  };
})();
